/* eslint-disable react-hooks/exhaustive-deps */
import {
  Autocomplete,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid2,
  IconButton,
  OutlinedInput,
  Stack,
  TextField,
} from '@mui/material';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import { isEmpty, isNull, isNumber, upperFirst } from 'lodash';
import { CloseIcon, DownIcon, UpIcon } from '../../../shared/components/AppIcons';
import { TagType } from '../../../../data/types/tags.interface';
import ConfirmationDialog from '../../../shared/components/ConfirmationDialog';
import ContentMarketingService from '../../services/contentMarketing.services';
import { MarketingPlatformCategories, MarketingPlatforms } from '../../../shared/constants';
import FinalFileDetailsForMarketing from './finalFileDetailsForMarketing';
import Toaster from '../../../shared/components/Toaster';
import FileUploader from '../../../shared/components/FileUploader';
import {
  AddMarketingPlatformProps,
  MarketingPlatform,
  MarketingPlatFormFileDefaultValue,
} from '../../../../data/types/contentMarketing.interface';
import { ContentResponse } from '../../../../data/types/content.interface';
import theme from '../../../../core/theme';
import LoadingBackdrop from '../../../shared/components/LoadingBackdrop';
import { default as TimezoneSelect } from 'react-timezone-select';

const useStyles = makeStyles({
  paper: {
    minWidth: '965px',
    height: 'auto',
    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.06)',
    '& .MuiInputBase-input, .MuiOutlinedInput-input': {
      font: 'Lato !important',
    },
    '& .MuiOutlinedInput-root': {
      height: '40px',
      fontFamily: 'Lato',
    },
    '& .form-control': {
      borderRadius: '4px !important',
    },
  },
  header: {
    fontSize: '28px',
  },
  close: {
    fontSize: '50px !important',
    color: '#5F6774 !important',
    '& path': {
      fill: `#2C2C2C !important`,
    },
  },
  list: {
    margin: '11px 0px 11px 34px',
    '& .MuiAutocomplete-inputRoot': {
      height: '40px',
    },
    '& .MuiStack-root': {
      border: '1px solid #E1E3E8',
      borderRadius: '4px',
    },
    '& input': {
      paddingLeft: '14px !important',
    },
    '& .MuiInput-underline:before': {
      borderBottom: 'none',
    },
    '& .MuiInput-underline:after': {
      borderBottom: 'none',
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled, .Mui-error):before': {
      borderBottom: 'none',
    },
  },
  inputBox: {
    height: '40px',
    padding: '8px',
    border: '1px solid #E1E3E8',
    borderRadius: '4px',
    '&.disableField': {
      backgroundColor: '#E1E3E8',
    },
    '&.disableText': {
      color: '#E1E3E8',
    },
  },
  editDetails: {
    width: '122px',
    height: '38px',
    backgroundColor: '#F67570 !important',
    color: '#fff !important',
    boxShadow: 'none !important',
    '& g path': {
      fill: '#ffffff !important',
    },
    '&.disable': {
      backgroundColor: '#848080 !important',
      cursor: 'none',
    },
  },
  cancel: {
    color: '#F67570 !important',
    marginRight: '24px !important',
    fontFamily: 'Lato !important',
  },
  button: {
    border: 'none !important',
    '& path': {
      fill: '#F67570 !important',
    },
  },
  smallText: {
    fontSize: '16px',
    color: theme.palette.outlineInactive.main,
  },
  error: {
    color: '#F57570',
  },
  linkOverFlow: { width: '49%', textOverflow: 'ellipsis', overflow: 'hidden' },
  timeZone: {
    width: '50%',
    '& span': {
      display: 'none',
    },
  },
  outlineInputField: {
    height: '40px',
    // Default border
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E1E3E8 !important',
    },
    // Border on hover
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E1E3E8 !important',
    },
    // Border on focus
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E1E3E8 !important',
      borderWidth: '1px !important',
    },
    // Remove native input outline
    '& input': {
      outline: 'none !important',
    },
  },
  dropdownIcon: {
    fontSize: '42px !important',
    '& path': {
      fill: '#5F6774 !important',
    },
  },
});

interface Props {
  setOpenDetailsModal: Dispatch<SetStateAction<boolean>>;
  id: number | null;
  assetType: string;
  setToasterMessage?: Dispatch<SetStateAction<string>>;
  setRefresh: Dispatch<SetStateAction<boolean>>;
  target?: string;
}

const MarketingPlatformContentDetails: React.FC<Props> = ({
  setOpenDetailsModal,
  assetType,
  id,
  setToasterMessage,
  setRefresh,
  target,
}) => {
  const classes = useStyles();
  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    getValues,
  } = useForm<AddMarketingPlatformProps>({
    defaultValues: MarketingPlatFormFileDefaultValue,
    mode: 'all',
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [cont, setCont] = useState<ContentResponse | null>(null);
  const [marketingDetails, setMarketingDetails] = useState<MarketingPlatform>();
  const [selectedPlatform, setSelectedPlatform] = useState<string>('YouTube');
  const [htmlTags, setHTMLTags] = useState('');
  const [publisher, setPublisher] = useState('');
  const [keywords, setKeywords] = useState('');
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [scheduledDate, setScheduledDate] = useState<Date | null>(null);
  const [timezone, setTimezone] = useState<string>('');
  const [show, setShow] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState(false);
  const [file, setFile] = useState<string>('');
  const [mp3File, setMP3File] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('Regular'); /* short, regular */
  const [open, setOpen] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const getToaster = () => {
    return <Toaster show={show} setShow={setShow} message={`Content Removed successfully`} />;
  };

  const getMarketingDetails = (marketingPlatformDetails: MarketingPlatform[]) => {
    const marketingData = marketingPlatformDetails.find((details) => details.platform === selectedPlatform);
    setMarketingDetails(marketingData);
    setValue('title', marketingData?.title || '');
    setValue('text', marketingData?.text || '');
    setValue('htmlTags', marketingData?.htmlTags || '');
    setValue('thumbnail', marketingData?.thumbnail || '');
    setValue('musicFileLicense', marketingData?.musicFileLicense || '');
    setValue('publisher', marketingData?.publisher || '');
    setValue('keywords', marketingData?.keywords || '');
    setValue('internalNote', marketingData?.internalNote || '');
    if (marketingData?.scheduledDate) {
      setScheduledDate(new Date(marketingData?.scheduledDate));
    }
    setTimezone(marketingData?.timezone || '');
  };

  const getContentDetails = async (id: number) => {
    try {
      const content = await ContentMarketingService.getContentDetails(assetType || 'video', id, selectedPlatform);
      setCont(content);
      getMarketingDetails(content.marketingPlatformDetails || []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (isNumber(id)) {
      getContentDetails(id);
    }
  }, [id]);

  useEffect(() => {
    if (!isNull(cont)) {
      getMarketingDetails(cont.marketingPlatformDetails);
    }
  }, [selectedPlatform]);

  const handleChange = (e: React.SyntheticEvent<Element, Event>, value: TagType | null) => {
    if (value) {
      setSelectedPlatform(value?.name);
    }
  };

  const handleTypeChange = (e: React.SyntheticEvent<Element, Event>, value: TagType | null) => {
    if (value) {
      setSelectedCategory(value?.name);
    }
  };

  const handleConfirm = async (form?: AddMarketingPlatformProps) => {
    try {
      setIsLoading(true);
      const formData = new FormData();

      if (assetType === 'video') {
        if (file) {
          const files = file || (form?.thumbnail as File);
          formData.append('thumbnail', files);
        } else {
          setErrorMessage('please upload thumbnail');
          setIsLoading(false);
          return;
        }
      }
      if (mp3File) {
        const mp3Files = mp3File || (form?.musicFileLicense as File);
        formData.append('musicFileLicense', mp3Files);
      }
      const config = { headers: { 'content-type': 'multipart/form-data' } };
      formData.append('platform', selectedPlatform);
      formData.append('htmlTags', htmlTags);
      formData.append('title', title);
      formData.append('text', text);
      formData.append('internalNote', internalNote);
      formData.append('assetContentTypeName', assetType);
      if (!isNull(scheduledDate)) {
        formData.append('scheduledDate', moment(scheduledDate).toLocaleString());
        formData.append('timezone', timezone);
      }
      formData.append('contentFormat', selectedCategory);
      formData.append('publisher', publisher);
      formData.append('keywords', keywords);
      if (id && !isEmpty(file || marketingDetails?.thumbnail)) {
        const response = await ContentMarketingService.updateContentMarketingDetails(
          id,
          assetType,
          selectedPlatform,
          formData,
          config
        );
        if (response) {
          setIsLoading(false);
          setOpenDetailsModal(false);
        }
      }
      setOpenDetailsModal(false);
    } catch (error) {
      setIsLoading(false);
      setErrorMessage('Please try again');
    }
  };

  const onSubmit: SubmitHandler<AddMarketingPlatformProps> = async (form) => {
    setIsFormSubmitted(true);
    handleConfirm(form);
    if (setToasterMessage) {
      setToasterMessage('Content uploaded successfully');
    }
  };

  const handleDelete = () => {
    setShowAlert(true);
  };

  const confirmDelete = async () => {
    try {
      if (cont) {
        await ContentMarketingService.delete(assetType, cont?.id, selectedPlatform);
      }
      setShow(true);
      setShowAlert(false);
      setOpenDetailsModal(false);
    } catch (err) {
      setShow(false);
      setShowAlert(true);
    }
  };

  return (
    <>
      <Dialog
        open
        aria-modal="true"
        role="dialog"
        onClose={() => setOpenDetailsModal(false)}
        PaperProps={{
          onClick: (e: any) => e.stopPropagation(),
          className: classes.paper,
        }}
      >
        <DialogTitle>
          <Grid2 container justifyContent="space-between">
            <Grid2>
              <span className={classes.header}>{`Content Marketing File Details`}</span>
            </Grid2>
            <Grid2 container justifyContent="flex-end">
              <IconButton onClick={() => setOpenDetailsModal(false)} role="button" sx={{ padding: 0 }}>
                <CloseIcon viewBox="0 0 42 42" className={classes.close} />
              </IconButton>
            </Grid2>
          </Grid2>
          <Grid2>
            <Divider
              orientation="horizontal"
              flexItem
              sx={{
                backgroundColor: '#E1E3E8',
                paddingBottom: '1px',
              }}
            />
          </Grid2>
        </DialogTitle>

        <DialogContent sx={{ paddingBottom: '0px' }}>
          {errorMessage && errorMessage.length > 0 && (
            <Grid2 container justifyContent="center">
              <div className="text-danger mb-1 pb-5 text-center">{errorMessage}</div>
            </Grid2>
          )}

          {getToaster()}

          <form onSubmit={cont?.published ? handleSubmit(handleDelete) : handleSubmit(onSubmit)}>
            {cont && <FinalFileDetailsForMarketing cont={cont} setRefresh={setRefresh} />}

            <Grid2 container width="101%">
              <Grid2 container direction="column" ml={0} className={classes.list} width="48%">
                <label>Marketing Platform*</label>
                <Stack spacing={1}>
                  <Autocomplete
                    ref={register}
                    readOnly={cont?.published}
                    options={MarketingPlatforms}
                    getOptionLabel={(option: TagType) => option.name}
                    id="auto-highlight"
                    autoHighlight
                    value={MarketingPlatforms.filter((p) => p.name === selectedPlatform)[0] || MarketingPlatforms[0]}
                    onChange={handleChange}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    popupIcon={
                      open ? <UpIcon className={classes.dropdownIcon} /> : <DownIcon className={classes.dropdownIcon} />
                    }
                    renderInput={(params) => <TextField {...params} label="" variant="standard" />}
                  />
                </Stack>
                {isEmpty(selectedPlatform) && isFormSubmitted && (
                  <span className={`${classes.smallText} ${classes.error}`}>Production File name can not be empty</span>
                )}
              </Grid2>

              <Grid2 container direction="column" ml={2} className={classes.list} width="49%">
                <label>Type of Content*</label>
                <Stack spacing={1}>
                  <Autocomplete
                    ref={register}
                    readOnly={cont?.published}
                    options={MarketingPlatformCategories}
                    getOptionLabel={(option: TagType) => option.name}
                    id="auto-highlight"
                    autoHighlight
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    popupIcon={
                      open ? <UpIcon className={classes.dropdownIcon} /> : <DownIcon className={classes.dropdownIcon} />
                    }
                    value={MarketingPlatformCategories.filter((c) => c.name === selectedCategory)[0]}
                    onChange={handleTypeChange}
                    renderInput={(params) => <TextField {...params} label="" variant="standard" />}
                  />
                </Stack>
                {isEmpty(selectedCategory) && isFormSubmitted && (
                  <span className={`${classes.smallText} ${classes.error}`}>Production File name can not be empty</span>
                )}
              </Grid2>
            </Grid2>

            {/* Add/display details related to marketing */}
            <Grid2 container mt={2}>
              <Grid2 container direction="column" width="25%">
                <label>Title*</label>
                {cont?.published ? (
                  <span className={classes.inputBox} style={{ minHeight: '30px' }}>
                    {marketingDetails?.title}
                  </span>
                ) : (
                  <input
                    {...register('title', { required: true })}
                    name="title"
                    id="title"
                    className={`form-control ${errors.title && 'is-invalid'}`}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                )}
                {isEmpty(getValues('title')) && (
                  <span className={`${classes.smallText} ${classes.error}`}>Title can't be empty</span>
                )}
              </Grid2>
              <Grid2 container direction="column" ml={1} width="24%">
                <label>HTML Tags*</label>
                {cont?.published ? (
                  <span className={classes.inputBox} style={{ minHeight: '30px' }}>
                    {marketingDetails?.htmlTags}
                  </span>
                ) : (
                  <input
                    {...register('htmlTags', { required: true })}
                    name="htmlTags"
                    id="htmlTags"
                    className={`form-control ${errors.htmlTags && 'is-invalid'}`}
                    onChange={(e) => setHTMLTags(e.target.value)}
                  />
                )}
                {isEmpty(getValues('htmlTags')) && (
                  <span className={`${classes.smallText} ${classes.error}`}>HTML Tags can't be empty</span>
                )}
              </Grid2>
              <Grid2 container ml={1} direction="column" width="24%">
                <label>Text Editor*</label>
                {cont?.published ? (
                  <span className={classes.inputBox} style={{ minHeight: '30px' }}>
                    {marketingDetails?.text}
                  </span>
                ) : (
                  <input
                    {...register('text', { required: true })}
                    name="text"
                    id="text"
                    className={`form-control ${errors.text && 'is-invalid'}`}
                    onChange={(e) => setText(e.target.value)}
                  />
                )}
                {isEmpty(getValues('text')) && (
                  <span className={`${classes.smallText} ${classes.error}`}>Text can't be empty</span>
                )}
              </Grid2>
              <Grid2 container direction="column" ml={1} width="24%">
                <label>Internal Note</label>
                {cont?.published ? (
                  <span className={classes.inputBox} style={{ minHeight: '30px' }}>
                    {marketingDetails?.internalNote}
                  </span>
                ) : (
                  <input
                    {...register('internalNote')}
                    name="internalNote"
                    id="internalNote"
                    className={`form-control`}
                    onChange={(e) => setInternalNote(e.target.value)}
                  />
                )}
              </Grid2>
            </Grid2>

            <Grid2 container mt={2}>
              <Grid2 container direction="column" width="25%">
                <label>Keywords</label>
                {cont?.published ? (
                  <span className={classes.inputBox} style={{ minHeight: '30px' }}>
                    {marketingDetails?.keywords}
                  </span>
                ) : (
                  <input
                    {...register('keywords')}
                    name="keywords"
                    id="keywords"
                    className={`form-control`}
                    onChange={(e) => setKeywords(e.target.value)}
                  />
                )}
              </Grid2>
              <Grid2 container ml={1} direction="column" width="24%">
                <label>Publisher</label>
                {cont?.published ? (
                  <span className={classes.inputBox} style={{ minHeight: '30px' }}>
                    {marketingDetails?.publisher}
                  </span>
                ) : (
                  <input
                    {...register('publisher')}
                    name="publisher"
                    id="publisher"
                    className={`form-control`}
                    onChange={(e) => setPublisher(e.target.value)}
                  />
                )}
              </Grid2>
              <Grid2 container direction="column" ml={1} width="49%">
                <label>Scheduled Date & Time</label>
                <DatePicker
                  selected={scheduledDate}
                  onChange={(date: React.SetStateAction<Date | null>) => setScheduledDate(date)}
                  showTimeSelect
                  timeIntervals={5}
                  dateFormat="dd/MM/yyyy h:mm aa"
                  showDisabledMonthNavigation
                  className="form-control"
                  placeholderText="Select date and time"
                  disabled={cont?.published}
                />
              </Grid2>
            </Grid2>

            <Grid2 container direction="column" mt={2}>
              <label>Time Zone</label>
              <TimezoneSelect
                className={classes.timeZone}
                value={timezone || ''}
                onChange={(tz: { value: any }) => setTimezone(tz.value || tz)}
                isDisabled={cont?.published}
              />
            </Grid2>

            <Grid2 container mt={2} flexWrap="nowrap">
              <Grid2 container direction="column" className={classes.linkOverFlow}>
                <label>Thumbnail*</label>
                {!isEmpty(marketingDetails?.thumbnail || file) ? (
                  <a href={marketingDetails?.thumbnail} target="_blank" rel="noreferrer" style={{ color: '#DE5852' }}>
                    <FormControl fullWidth sx={{ height: '40px' }}>
                      <OutlinedInput
                        className={classes.outlineInputField}
                        value={marketingDetails?.thumbnail || file}
                      />
                    </FormControl>
                  </a>
                ) : (
                  <FileUploader
                    handleFileUpload={() => null}
                    setErrorMessage={setErrorMessage}
                    applyStyles={false}
                    id="thumbnail"
                    isContentUpload={false}
                    isPublished={cont?.published}
                    setSelectedFile={setFile}
                  />
                )}
                {isEmpty(file || marketingDetails?.thumbnail) && (
                  <span className={`${classes.smallText} ${classes.error}`}>Thumbnail is missing</span>
                )}
              </Grid2>
              <Grid2 container direction="column" ml={2} className={classes.linkOverFlow} flexWrap="nowrap">
                <label>Music File License</label>
                {marketingDetails?.musicFileLicense || mp3File ? (
                  <a
                    href={marketingDetails?.musicFileLicense}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: '#DE5852' }}
                  >
                    <FormControl sx={{ height: '40px' }} fullWidth>
                      <OutlinedInput
                        className={classes.outlineInputField}
                        value={marketingDetails?.musicFileLicense || mp3File}
                      />
                    </FormControl>
                  </a>
                ) : (
                  <FileUploader
                    handleFileUpload={() => null}
                    setErrorMessage={setErrorMessage}
                    applyStyles={false}
                    id="mfFile"
                    isContentUpload={false}
                    isPublished={cont?.published}
                    setSelectedFile={setMP3File}
                  />
                )}
              </Grid2>
            </Grid2>

            <Grid2 container mt={2}>
              <Grid2 container direction="column" style={{ width: '49%' }}>
                <label>Publish Status</label>
                <span
                  className={`${classes.inputBox} ${marketingDetails?.publishedDate ? 'disableText' : 'disableField'}`}
                  style={{ width: 'auto', minHeight: '40px' }}
                >
                  {marketingDetails && upperFirst(marketingDetails.publishedStatus)}
                </span>
              </Grid2>
              <Grid2 container direction="column" ml="15px" style={{ width: '49%' }}>
                <label>Published Date</label>
                <span
                  className={`${classes.inputBox} ${marketingDetails?.publishedDate ? 'disableText' : 'disableField'}`}
                  style={{ width: 'auto', minHeight: '40px' }}
                >
                  {marketingDetails?.publishedDate ? moment(marketingDetails?.publishedDate).format('YYYY-MM-DD') : ''}
                </span>
              </Grid2>
            </Grid2>

            <Grid2 container direction="column" mt={2} mb={target === 'marketing' ? 0 : 2}>
              <label>Publishing Link</label>
              <span
                className={`${classes.inputBox} ${marketingDetails?.publishedDate ? 'disableText' : 'disableField'}`}
                style={{ width: '100%', border: '1px solid #DE5852' }}
              >
                <a
                  href={cont?.videoUrl}
                  target="_blank"
                  rel="noreferrer"
                  aria-disabled
                  style={{ color: '#DE5852', textDecoration: 'none' }}
                >
                  {marketingDetails?.publishingLink}
                </a>
              </span>
            </Grid2>

            {target === 'marketing' && (
              <Grid2 container justifyContent="flex-end" mt={3} mb={2}>
                <Button
                  size="small"
                  variant="text"
                  type="button"
                  onClick={() => setOpenDetailsModal(false)}
                  className={classes.cancel}
                >
                  cancel
                </Button>

                <Button
                  size="small"
                  color="primary"
                  variant="contained"
                  className={`${classes.editDetails} ${!isEmpty(errors) ? 'disable' : ''}`}
                  type="submit"
                >
                  <div className="caption" style={{ fontSize: 'h5', textTransform: 'none' }}>
                    {cont?.published ? 'Unpublish' : 'Publish'}
                  </div>
                </Button>
              </Grid2>
            )}
          </form>
          <LoadingBackdrop openBackdrop={isLoading} />
        </DialogContent>
        {showAlert && (
          <ConfirmationDialog fileCount={1} action="delete" confirm={confirmDelete} setShowAlert={setShowAlert} />
        )}
      </Dialog>
    </>
  );
};

export default MarketingPlatformContentDetails;
